"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Sparkles, 
  Zap, 
  Target, 
  RefreshCw, 
  Brain, 
  Clock,
  ArrowRight,
  Download,
  CheckCircle2,
  Twitter,
  Chrome
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { 
  trackChromeStoreClick, 
  trackDemoVideoPlay,
  ConversionFunnel 
} from "@/lib/analytics";

export default function PrismXLanding() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMounted, setIsMounted] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const demoRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true, margin: "-100px" });
  const isFeaturesInView = useInView(featuresRef, { once: true, margin: "-100px" });
  const isDemoInView = useInView(demoRef, { once: true, margin: "-100px" });

  useEffect(() => {
    setIsMounted(true);
    
    // Track page load (Funnel Step 1)
    ConversionFunnel.step1_PageLoad();
    
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Track Hero View (Funnel Step 2)
  useEffect(() => {
    if (isHeroInView) {
      ConversionFunnel.step2_HeroView();
    }
  }, [isHeroInView]);

  // Track Features View (Funnel Step 3)
  useEffect(() => {
    if (isFeaturesInView) {
      ConversionFunnel.step3_FeaturesView();
    }
  }, [isFeaturesInView]);

  // Track Demo View (Funnel Step 4)
  useEffect(() => {
    if (isDemoInView) {
      ConversionFunnel.step4_DemoOrComparison('demo');
    }
  }, [isDemoInView]);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - Left */}
            <Link href="/" className="flex items-center gap-2 group">
              <Image 
                src="/favicon.svg" 
                alt="PrismX Logo" 
                width={32} 
                height={32} 
                className="w-8 h-8 group-hover:scale-110 transition-transform" 
              />
              <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400">
                PrismX
              </span>
            </Link>

            {/* Navigation Links - Center */}
            <div className="hidden md:flex items-center gap-8">
              <a 
                href="#features" 
                className="text-gray-300 hover:text-purple-400 transition-colors font-medium"
              >
                Features
              </a>
              <Link 
                href="/blogs" 
                className="text-gray-300 hover:text-purple-400 transition-colors font-medium"
              >
                Blog
              </Link>
              <a 
                href="https://youtu.be/lmTWXByP0kY" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-purple-400 transition-colors font-medium"
                onClick={() => trackDemoVideoPlay()}
              >
                Demo
              </a>
            </div>

            {/* CTA Button - Right */}
            <Link 
              href="https://chromewebstore.google.com/detail/Prism%20AI%20for%20Twitter/ceonklipfigpiceejekbfmhbbmhidojb?hl=en&authuser=0" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => {
                trackChromeStoreClick('navbar');
                ConversionFunnel.step5_CTAClick();
              }}
            >
              <Button 
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-2 rounded-full group shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)]"
              >
                <Chrome className="w-4 h-4 mr-2" />
                Add to Chrome
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Animated Background - Balanced */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Subtle mouse tracking gradient */}
        <div 
          className="absolute inset-0 opacity-20 transition-opacity duration-300"
          style={{
            background: `radial-gradient(circle 700px at ${mousePosition.x}px ${mousePosition.y}px, rgba(139, 92, 246, 0.08), transparent 70%)`
          }}
        />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-10" />
        
        {/* Radial vignette */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-black/60" />
        
        {/* Floating Orbs - More visible */}
        <motion.div
          animate={{
            x: [0, 120, 0],
            y: [0, -80, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-20 w-[600px] h-[600px] bg-purple-500/15 rounded-full blur-[140px]"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 80, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 26,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-20 right-20 w-[650px] h-[650px] bg-blue-500/15 rounded-full blur-[140px]"
        />
        
        {/* Additional accent orb */}
        <motion.div
          animate={{
            x: [0, 70, 0],
            y: [0, -60, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4
          }}
          className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-pink-500/12 rounded-full blur-[120px]"
        />
        
        {/* Animated particles - Client-side only to prevent hydration mismatch */}
        {isMounted && [...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -1000],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: Math.random() * 12 + 12,
              repeat: Infinity,
              delay: Math.random() * 8,
              ease: "linear"
            }}
            className="absolute w-0.5 h-0.5 bg-purple-400/40 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: '100%',
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center px-6 py-20 pt-32"
      >
        
        <div className="max-w-7xl mx-auto text-center z-10 relative">
          <div className="mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 mb-8">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-purple-300">Introducing PrismX</span>
            </div>
          </div>

          <h1
            className="text-6xl md:text-8xl font-bold mb-6 leading-tight"
            style={{ 
              fontSize: 'clamp(3.75rem, 8vw, 6rem)',
            }}
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-300 via-pink-300 to-blue-300">
              Twitter, Without
            </span>
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400">
              The Time Tax
            </span>
          </h1>

          <p
            className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-12"
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 font-semibold">AI</span> that writes in <span className="text-white font-semibold">your voice</span>. <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-semibold">Two clicks</span>. <span className="text-white font-semibold">No setup</span>. 
            <br />
            <span className="text-white font-semibold">
              Create professional tweets in <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400 font-bold">30 seconds</span>, not <span className="text-gray-400 line-through">30 minutes</span>.
            </span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              href="https://chromewebstore.google.com/detail/Prism%20AI%20for%20Twitter/ceonklipfigpiceejekbfmhbbmhidojb?hl=en&authuser=0" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => {
                trackChromeStoreClick('hero');
                ConversionFunnel.step5_CTAClick();
              }}
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-full group shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.6)]"
              >
                <Chrome className="w-5 h-5 mr-2" />
                Add to Chrome - <span className="font-bold">Free in Beta</span>
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link 
              href="https://youtu.be/lmTWXByP0kY" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => trackDemoVideoPlay()}
            >
              <Button 
                size="lg" 
                variant="outline"
                className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10 px-8 py-6 text-lg rounded-full"
              >
                Watch Demo
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="text-center p-6 rounded-2xl bg-purple-500/10 border border-purple-400/30 backdrop-blur-sm hover:bg-purple-500/20 transition-all">
              <div className="text-5xl font-bold text-purple-300 mb-2">2</div>
              <div className="text-sm text-gray-200 mt-1 font-medium">Clicks to Start</div>
            </div>
            <div className="text-center p-6 rounded-2xl bg-blue-500/10 border border-blue-400/30 backdrop-blur-sm hover:bg-blue-500/20 transition-all">
              <div className="text-5xl font-bold text-blue-300 mb-2">30s</div>
              <div className="text-sm text-gray-200 mt-1 font-medium">Per Tweet</div>
            </div>
            <div className="text-center p-6 rounded-2xl bg-pink-500/10 border border-pink-400/30 backdrop-blur-sm hover:bg-pink-500/20 transition-all">
              <div className="text-5xl font-bold text-pink-300 mb-2">0</div>
              <div className="text-sm text-gray-200 mt-1 font-medium">Setup Required</div>
            </div>
          </div>
        </div>

      </section>

      {/* Demo Video Section */}
      <section ref={demoRef} className="relative py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400">
              See It In Action
            </h2>
            <p className="text-xl text-gray-400">
              Watch how PrismX transforms your Twitter workflow
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative aspect-video rounded-3xl overflow-hidden border border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-xl"
          >
            {!showVideo ? (
              /* YouTube Thumbnail - Click to Load (No cookies until clicked) */
              <div 
                className="absolute inset-0 flex items-center justify-center cursor-pointer group"
                onClick={() => {
                  setShowVideo(true);
                  trackDemoVideoPlay();
                }}
              >
                {/* YouTube Thumbnail Image */}
                <Image
                  src="https://img.youtube.com/vi/lmTWXByP0kY/maxresdefault.jpg"
                  alt="PrismX Demo Video Thumbnail"
                  fill
                  className="object-cover"
                  priority
                />
                
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors"></div>
                
                {/* Play Button */}
                <div className="relative z-10 w-24 h-24 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_0_40px_rgba(139,92,246,0.6)]">
                  <div className="w-0 h-0 border-t-[15px] border-t-transparent border-l-[25px] border-l-white border-b-[15px] border-b-transparent ml-2"></div>
                </div>
                
                {/* Click to Play Text */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center z-10">
                  <p className="text-white text-lg font-semibold mb-1 drop-shadow-lg">Click to watch demo</p>
                  <p className="text-gray-200 text-sm drop-shadow-lg">Full walkthrough of PrismX features</p>
                </div>
              </div>
            ) : (
              /* YouTube Video Embed - Privacy Enhanced (Loads only after click) */
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/lmTWXByP0kY?autoplay=1"
                title="PrismX Demo Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            )}

            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-3 h-3 rounded-full bg-red-500 z-10"></div>
            <div className="absolute top-4 left-10 w-3 h-3 rounded-full bg-yellow-500 z-10"></div>
            <div className="absolute top-4 left-16 w-3 h-3 rounded-full bg-green-500 z-10"></div>
          </motion.div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="relative py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl font-bold mb-6">
              The Twitter Paradox
            </h2>
            <p className="text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              You know you should be on <span className="text-blue-400 font-semibold">Twitter</span>. You have insights to share. 
              <br />
              <span className="text-red-400 font-semibold">But crafting the perfect tweet takes <span className="font-bold">30 minutes</span> you don&apos;t have.</span>
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Clock, title: "Too Time-Consuming", desc: "Writing professional tweets takes forever" },
              { icon: Brain, title: "Mental Overhead", desc: "Thinking about tone, length, and engagement" },
              { icon: Target, title: "Consistency Problem", desc: "Hard to maintain regular presence" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                className="h-full"
              >
                <Card className="p-8 bg-gradient-to-br from-gray-900/50 to-gray-800/50 border-gray-700/50 backdrop-blur-xl hover:border-red-500/50 transition-all h-full">
                  <item.icon className="w-12 h-12 text-red-400 mb-4" />
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative py-24 px-6 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Simple Setup, Instant Results
            </h2>
            <p className="text-lg text-gray-400">
              From installation to your first tweet in under 2 minutes
            </p>
          </motion.div>

          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 via-blue-500 to-pink-500 -translate-x-1/2 hidden md:block" />

            {[
              {
                number: "01",
                title: "Install & Connect",
                desc: "Add PrismX to Chrome. Click 'Connect Twitter'. That's it.",
                icon: Download
              },
              {
                number: "02",
                title: "AI Learns Your Voice",
                desc: "PrismX analyzes your existing tweets to understand your unique style.",
                icon: Brain
              },
              {
                number: "03",
                title: "Start Creating",
                desc: "Type your idea in Twitter's compose box. AI writes it. You post it.",
                icon: Sparkles
              }
            ].map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: idx * 0.2 }}
                className="relative mb-12 last:mb-0"
              >
                <div className={`flex items-center gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="flex-1">
                    <Card className="p-6 bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-500/30 backdrop-blur-xl">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                          <step.icon className="w-6 h-6" />
                        </div>
                        <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                          {step.number}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                      <p className="text-gray-400">{step.desc}</p>
                    </Card>
                  </div>
                  <div className="hidden md:block w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex-shrink-0 relative z-10" />
                  <div className="flex-1 hidden md:block" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Redesigned */}
      <section id="features" ref={featuresRef} className="relative py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isFeaturesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400">
              Powerful Features, Zero Complexity
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Everything you need to master Twitter without the time investment
            </p>
          </motion.div>

          <div className="space-y-24">
            {/* Feature 1 - Compose Posts */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              <div className="order-2 md:order-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                    <Sparkles className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="text-sm text-purple-400 font-semibold uppercase tracking-wider mb-1">Feature 1</div>
                    <h3 className="text-4xl font-bold">Compose Posts Instantly</h3>
                  </div>
                </div>
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  Type your raw idea directly in Twitter&apos;s compose box. PrismX transforms it into an engaging, on-brand tweet that sounds exactly like you.
                  <span className="block mt-2 text-purple-400">From idea to tweet in 30 seconds.</span>
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Works directly in Twitter&apos;s interface</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Matches your unique writing style</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Lightning-fast generation</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 md:order-2 flex justify-center">
                <div className="relative w-[450px]">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 blur-3xl"></div>
                  <div className="relative rounded-2xl border-2 border-purple-500/30 overflow-hidden bg-black p-3">
                    <Image 
                      src="/screenshots/prism_compose.png" 
                      alt="PrismX Post Composer"
                      width={450}
                      height={750}
                      className="w-full h-auto rounded-xl"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Feature 2 - Generate Replies & Quotes */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              <div className="flex justify-center">
                <div className="relative w-[450px]">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 blur-3xl"></div>
                  <div className="relative rounded-2xl border-2 border-blue-500/30 overflow-hidden bg-black p-3">
                    <Image 
                      src="/screenshots/prism_reply.png" 
                      alt="PrismX Reply Generator"
                      width={450}
                      height={750}
                      className="w-full h-auto rounded-xl"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <Twitter className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="text-sm text-blue-400 font-semibold uppercase tracking-wider mb-1">Feature 2</div>
                    <h3 className="text-4xl font-bold">Smart Replies & Quotes</h3>
                  </div>
                </div>
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  Generate thoughtful replies and quote tweets that amplify your engagement. PrismX understands context and crafts responses that match your voice.
                  <span className="block mt-2 text-blue-400">Engage authentically, at scale.</span>
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Context-aware reply generation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Intelligent quote tweet suggestions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Maintain authentic engagement</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Feature 3 - Refine Generations */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              <div className="order-2 md:order-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                    <RefreshCw className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="text-sm text-pink-400 font-semibold uppercase tracking-wider mb-1">Feature 3</div>
                    <h3 className="text-4xl font-bold">Refine & Perfect</h3>
                  </div>
                </div>
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  Not quite right? Give feedback in plain English and regenerate instantly. Make it more casual, add humor, adjust the tone, or change the angle.
                  <span className="block mt-2 text-pink-400">Iterate until it&apos;s exactly what you want.</span>
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-pink-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Natural language feedback</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-pink-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Instant regeneration</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-pink-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Unlimited iterations</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 md:order-2 flex justify-center">
                <div className="relative w-[450px]">
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-rose-500/20 blur-3xl"></div>
                  <div className="relative rounded-2xl border-2 border-pink-500/30 overflow-hidden bg-black p-3">
                    <Image 
                      src="/screenshots/prism_feedback.png" 
                      alt="PrismX Refinement System"
                      width={450}
                      height={750}
                      className="w-full h-auto rounded-xl"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Feature 4 - Brand Compass */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              <div className="flex justify-center">
                <div className="relative w-[450px]">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 blur-3xl"></div>
                  <div className="relative rounded-2xl border-2 border-indigo-500/30 overflow-hidden bg-black p-3">
                    <Image 
                      src="/screenshots/prismx_brand_compass.png" 
                      alt="PrismX Brand Compass"
                      width={450}
                      height={750}
                      className="w-full h-auto rounded-xl"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <Brain className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="text-sm text-indigo-400 font-semibold uppercase tracking-wider mb-1">Feature 4</div>
                    <h3 className="text-4xl font-bold">Brand Compass</h3>
                  </div>
                </div>
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  Get a comprehensive analysis of your brand voice, audience, and content strategy. Download as a detailed PDF report.
                  <span className="block mt-2 text-indigo-400">Perfect for sharing with your team or clients.</span>
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-indigo-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Complete brand voice analysis</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-indigo-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Audience insights and demographics</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-indigo-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Downloadable PDF report</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Feature 5 - Tweet Finder */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              <div className="order-2 md:order-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                    <Target className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="text-sm text-teal-400 font-semibold uppercase tracking-wider mb-1">Feature 5</div>
                    <h3 className="text-4xl font-bold">Tweet Finder</h3>
                  </div>
                </div>
                <p className="text-xl text-gray-300 leading-relaxed mb-6">
                  Discover relevant tweets to engage with based on your brand and interests. AI curates conversations that matter, helping you connect with the right audience.
                  <span className="block mt-2 text-teal-400">Find opportunities to grow your presence.</span>
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">AI-powered tweet discovery</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Relevant conversations matched to your brand</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-400">Smart filtering by date and context</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 md:order-2 flex justify-center">
                <div className="relative w-[450px]">
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-cyan-500/20 blur-3xl"></div>
                  <div className="relative rounded-2xl border-2 border-teal-500/30 overflow-hidden bg-black p-3">
                    <Image 
                      src="/screenshots/prism_tweet_finder.png" 
                      alt="PrismX Tweet Finder"
                      width={450}
                      height={750}
                      className="w-full h-auto rounded-xl"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="relative py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4">
              Built For Busy Professionals
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              You know you should be on <span className="text-blue-400 font-semibold">Twitter</span>. You have <span className="text-white font-semibold">insights</span> to share. 
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 font-semibold">PrismX removes the time barrier.</span>
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "The Time-Strapped Professional",
                desc: "You have expertise but not hours to craft tweets",
                gradient: "from-purple-900/20 to-blue-900/20",
                border: "border-purple-500/30"
              },
              {
                title: "The Hesitant Poster",
                desc: "You avoid Twitter because writing 'good tweets' feels like work",
                gradient: "from-blue-900/20 to-pink-900/20",
                border: "border-blue-500/30"
              },
              {
                title: "The Inconsistent Creator",
                desc: "You want consistent presence without the daily grind",
                gradient: "from-pink-900/20 to-purple-900/20",
                border: "border-pink-500/30"
              }
            ].map((persona, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              >
                <Card className={`p-8 bg-gradient-to-br ${persona.gradient} border ${persona.border} backdrop-blur-xl h-full hover:scale-105 transition-transform`}>
                  <CheckCircle2 className="w-12 h-12 text-purple-400 mb-4" />
                  <h3 className="text-xl font-bold mb-3">{persona.title}</h3>
                  <p className="text-gray-400">{persona.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <Card className="p-8 bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-purple-500/30 backdrop-blur-xl inline-block">
              <p className="text-xl text-gray-300">
                <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                  You don&apos;t need to be a content creator or marketer.
                </span>
                <br />
                If you can tweet, you can use PrismX.
              </p>
            </Card>
          </motion.div>
        </div>
      </section>
      

      {/* Before/After Comparison */}
      <section className="relative py-32 px-6 bg-gradient-to-b from-transparent via-blue-900/10 to-transparent">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold mb-4">
              The Time Difference
            </h2>
            <p className="text-xl text-gray-400">
              See what PrismX really means for your workflow
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Before */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Card className="p-8 bg-gradient-to-br from-red-900/20 to-gray-900/20 border-red-500/30 backdrop-blur-xl h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <h3 className="text-2xl font-bold">Before PrismX</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    { time: "10 min", task: "Think about what to say" },
                    { time: "15 min", task: "Write and rewrite" },
                    { time: "5 min", task: "Edit for tone and length" },
                    { time: "5 min", task: "Second-guess and revise" },
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-4 text-gray-400">
                      <span className="text-red-400 font-mono font-bold w-16">{item.time}</span>
                      <span>{item.task}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-red-500/30">
                  <div className="text-3xl font-bold text-red-400">35 minutes</div>
                  <div className="text-gray-500">per tweet</div>
                </div>
              </Card>
            </motion.div>

            {/* After */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Card className="p-8 bg-gradient-to-br from-green-900/20 to-blue-900/20 border-green-500/30 backdrop-blur-xl h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <h3 className="text-2xl font-bold">With PrismX</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    { time: "10 sec", task: "Type your idea" },
                    { time: "10 sec", task: "AI generates tweet" },
                    { time: "10 sec", task: "Quick review or refine" },
                    { time: "5 sec", task: "Post" },
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-4 text-gray-400">
                      <span className="text-green-400 font-mono font-bold w-16">{item.time}</span>
                      <span>{item.task}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-green-500/30">
                  <div className="text-3xl font-bold text-green-400">35 seconds</div>
                  <div className="text-gray-500">per tweet</div>
                </div>
              </Card>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <div className="inline-block px-8 py-4 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/50">
              <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                60x Faster
              </span>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="relative py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">
              Start Creating Better Tweets Today
            </h2>
            <p className="text-2xl text-gray-400 mb-12">
              <span className="text-white font-semibold">No setup</span>. <span className="text-white font-semibold">No learning curve</span>. Just <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400 font-bold">professional tweets in seconds</span>.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <Link 
                href="https://chromewebstore.google.com/detail/Prism%20AI%20for%20Twitter/ceonklipfigpiceejekbfmhbbmhidojb?hl=en&authuser=0" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => {
                  trackChromeStoreClick('final_cta');
                  ConversionFunnel.step5_CTAClick();
                }}
              >
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-12 py-8 text-xl rounded-full group"
                >
                  <Chrome className="w-6 h-6 mr-3" />
                  Add to Chrome - Free in Beta
                  <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-8 text-gray-400">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span><span className="text-green-400 font-semibold">Free in beta</span> - no credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-400" />
                <span><span className="text-blue-400 font-semibold">2-minute</span> setup</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-purple-400" />
                <span><span className="text-purple-400 font-semibold">No credit card</span></span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <Image src="/favicon.svg" alt="PrismX Logo" width={32} height={32} className="w-8 h-8" loading="lazy" />
            <span className="text-xl font-bold">PrismX</span>
          </div>
          <div className="flex gap-8 text-gray-400">
            <a href="/about" className="hover:text-purple-400 transition-colors">About</a>
            <a href="/privacy" className="hover:text-purple-400 transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-purple-400 transition-colors">Terms</a>
            <a href="mailto:vinayak.sachdeva@codenation.co.in" className="hover:text-purple-400 transition-colors">Support</a>
          </div>
          <div className="text-gray-500">
            © 2025 PrismX. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Custom CSS for grid pattern and smooth scrolling */}
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(99, 102, 241, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </div>
  );
}

